import { request } from 'utils/request';
import { header, getUUIDToken } from 'utils/auth';

const fetchMyautoiqTemplateGenerationSettingsFromAPI = async ({
  manufacturerId,
}: {
  manufacturerId: string;
}): Promise<any> => {
  try {
    const requestURL = `/api/v2/myautoiq-settings/manufacturer=${manufacturerId}?type=TEMPLATE_GENERATION_CONFIG`;
    const response: any = await request(requestURL, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
      }),
    });

    if (!response) {
      throw new Error('Failed to fetch myautoiq template generation settings from API');
    }
    return await response;
  } catch (error) {
    console.error('Error fetching myautoiq template generation settings:', error);
    throw error;
  }
};

const updateMyautoiqTemplateGenerationSettingsFromAPI = async ({
  manufacturerId,
  settingsId,
  data,
}: {
  manufacturerId: string;
  settingsId: string;
  data: any;
}): Promise<any> => {
  try {
    const requestURL = `/api/v2/myautoiq-settings/manufacturer=${manufacturerId}/${settingsId}`;
    const response: any = await request(requestURL, {
      method: 'PUT',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
      }),
      body: JSON.stringify(data),
    });

    if (!response) {
      throw new Error('Failed to update myautoiq template generation settings from API');
    }
    return await response;
  } catch (error) {
    console.error('Error updating myautoiq template generation settings:', error);
    throw error;
  }
};

const fetchSegmentsForDealerFromAPI = async (
  dealerId: string,
): Promise<any> => {
  try {
    const requestURL = `/api/v1/segments/${dealerId}`;
    const response: any = await request(requestURL, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
        'uuid-token': getUUIDToken(),
      }),
    });
    if (!response) throw new Error('Failed to fetch segments');
    return response;
  } catch (error) {
    console.error('Error fetching segments:', error);
    throw error;
  }
};

export const fetchMyautoiqTemplateGenerationSettings = async ({
  manufacturerId,
}: {
  manufacturerId: string;
}): Promise<any> => {
  return fetchMyautoiqTemplateGenerationSettingsFromAPI({ manufacturerId });
};

export const fetchSegmentsForDealer = async (
  dealerId: string,
): Promise<any> => {
  return fetchSegmentsForDealerFromAPI(dealerId);
};

export const updateMyautoiqTemplateGenerationSettings = async ({
  manufacturerId,
  settingsId,
  data,
}: {
  manufacturerId: string;
  settingsId: string;
  data: any;
}): Promise<any> => {
  return updateMyautoiqTemplateGenerationSettingsFromAPI({
    manufacturerId,
    settingsId,
    data,
  });
};

const initializeMyautoiqTemplateGenerationSettingsFromAPI = async ({
  manufacturerId,
}: {
  manufacturerId: string;
}): Promise<any> => {
  try {
    const requestURL = `/api/v2/myautoiq-settings/initialize?manufacturer_id=${manufacturerId}&type=TEMPLATE_GENERATION_CONFIG`;
    const response: any = await request(requestURL, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
      }),
    });

    if (!response) {
      throw new Error('Failed to initialize myautoiq template generation settings from API');
    }
    const res = await response;
    const status = res?.statusCode ?? res?.myAutoIQ_status_code ?? res?.errorCode;
    const msg = res?.message ?? res?.myAutoIQ_error_message;
    if (typeof status === 'number' && status >= 400) {
      throw new Error(msg || 'Failed to initialize myautoiq template generation settings');
    }
    return res;
  } catch (error) {
    console.error('Error initializing myautoiq template generation settings:', error);
    throw error;
  }
};

export const initializeMyautoiqTemplateGenerationSettings = async ({
  manufacturerId,
}: {
  manufacturerId: string;
}): Promise<any> => {
  return initializeMyautoiqTemplateGenerationSettingsFromAPI({ manufacturerId });
};
